import { useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'

import { Page } from '@/components/layout/page/page'
import { LoadingOverlay } from '@/modules/shared/components/LoadingOverlay'
import { getSoundsList } from '@/modules/tiktok-research/service'
import { TikTokMediaGridSkeleton } from '@/modules/tiktok-research/skeletons/TikTokMediaGridSkeleton.tsx'
import { TikTokSoundsFilters } from '@/modules/tiktok-research/sounds/ui/filters/TikTokSoundsFiltersForm.tsx'
import {
  type TikTokSoundsFiltersForm,
  defaultValues,
} from '@/modules/tiktok-research/sounds/ui/filters/validation.ts'
import { SoundsGrid } from '@/modules/tiktok-research/sounds/ui/sounds-grid/SoundsGrid.tsx'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

export const TikTokSounds = () => {
  const { ref, inView } = useInView()
  const [filters, setFilters] = useState<TikTokSoundsFiltersForm>(defaultValues)
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isFetching } =
    useInfiniteQuery(
      ['sounds', JSON.stringify(filters)],
      ({ pageParam = 1, signal }) => getSoundsList(filters, pageParam, signal),
      {
        getNextPageParam: (page, pages) =>
          page.last ? undefined : pages.length + 1,
        refetchOnMount: true,
      },
    )

  useEffect(() => {
    if (data?.pages[0]?.content.length) {
      trackFeatureUsage(TrackedFeatures.TiktokResearch)
    }
  }, [data])

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  const sounds = data?.pages
    .flatMap((page) => page.content)
    .filter(isTruthyAndNotEmpty)

  return (
    <Page
      title="Sounds"
      description="Viral sounds that are featured in trending videos"
      className="mb-8"
    >
      <TikTokSoundsFilters
        className="mb-8"
        value={filters}
        onChange={setFilters}
      />
      {isFetching && !isFetchingNextPage ? (
        <>
          <LoadingOverlay message="Loading bulk sound data..." />
          <TikTokMediaGridSkeleton />
        </>
      ) : (
        <SoundsGrid
          sounds={sounds ?? []}
          lastSoundElement={ref}
          isFetching={isFetching}
        />
      )}
    </Page>
  )
}
